"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const Enums_1 = require("../types/Enums");
const uuid_1 = require("uuid");
const MeasureBundleHelpers_1 = require("../helpers/MeasureBundleHelpers");
const CustomErrors_1 = require("../types/errors/CustomErrors");
const DetailedResultsHelpers_1 = require("../helpers/DetailedResultsHelpers");
const AbstractMeasureReportBuilder_1 = require("./AbstractMeasureReportBuilder");
class MeasureReportBuilder extends AbstractMeasureReportBuilder_1.AbstractMeasureReportBuilder {
    constructor(measure, options) {
        super(measure, options);
        this.report = {
            id: (0, uuid_1.v4)(),
            resourceType: 'MeasureReport'
        };
        this.scoringCode = (0, MeasureBundleHelpers_1.getScoringCodeFromMeasure)(this.measure) || '';
        // if report type is specified use it, otherwise default to individual report.
        if (this.options.reportType) {
            this.isIndividual = this.options.reportType === 'individual';
        }
        else {
            this.isIndividual = true;
        }
        // determine if we should be calculating SDE, TODO: Support SDEs for summary/subject-list.
        this.calculateSDEs = this.options.calculateSDEs === true && this.isIndividual;
        this.calculateRAVs = this.options.calculateRAVs === true && this.isIndividual;
        this.patientCount = 0;
        this.numeratorAggregateMethod = '';
        this.denominatorAggregateMethod = '';
        this.setupBasicStructure();
        this.setupPopulationGroups();
    }
    getGroupScoringCode(group) {
        var _a;
        return (_a = (0, MeasureBundleHelpers_1.getScoringCodeFromGroup)(group)) !== null && _a !== void 0 ? _a : this.scoringCode;
    }
    setupBasicStructure() {
        // simple fields
        this.report.period = {
            start: this.options.measurementPeriodStart,
            end: this.options.measurementPeriodEnd
        };
        this.report.status = 'complete';
        this.report.type = this.isIndividual ? 'individual' : 'summary';
        // measure url from measure bundle
        this.report.measure = this.measure.url || 'UnknownMeasure'; // or some other default?
        this.report.contained = [];
        // add narrative if specified
        if (this.options.calculateHTML && this.isIndividual) {
            this.report.text = {
                status: 'generated',
                div: ''
            };
        }
    }
    setupPopulationGroups() {
        var _a;
        // create group population group based on measure resource
        this.report.group = [];
        this.report.evaluatedResource = [];
        // build population groups from measure resource
        (_a = this.measure.group) === null || _a === void 0 ? void 0 : _a.forEach(measureGroup => {
            var _a, _b;
            const group = {};
            if (measureGroup.id) {
                group.id = measureGroup.id;
            }
            group.population = [];
            const groupScoringCode = this.getGroupScoringCode(measureGroup);
            // build each population group with 0 for initial value
            (_a = measureGroup.population) === null || _a === void 0 ? void 0 : _a.forEach(measurePopulation => {
                var _a, _b, _c, _d, _e, _f, _g, _h, _j;
                if (groupScoringCode === Enums_1.MeasureScoreType.RATIO &&
                    ((_b = (_a = measurePopulation.code) === null || _a === void 0 ? void 0 : _a.coding) === null || _b === void 0 ? void 0 : _b[0].code) === 'measure-observation') {
                    if (measurePopulation.criteria.expression === 'Numerator Observations') {
                        this.numeratorAggregateMethod =
                            ((_d = (_c = measurePopulation.extension) === null || _c === void 0 ? void 0 : _c.find(e => e.url === 'http://hl7.org/fhir/us/cqfmeasures/StructureDefinition/cqfm-aggregateMethod')) === null || _d === void 0 ? void 0 : _d.valueCode) || '';
                    }
                    else if (measurePopulation.criteria.expression === 'Denominator Observations') {
                        this.denominatorAggregateMethod =
                            ((_f = (_e = measurePopulation.extension) === null || _e === void 0 ? void 0 : _e.find(e => e.url === 'http://hl7.org/fhir/us/cqfmeasures/StructureDefinition/cqfm-aggregateMethod')) === null || _f === void 0 ? void 0 : _f.valueCode) || '';
                    }
                }
                else {
                    // do not include measure observation population groups for ratio reports
                    const pop = {};
                    pop.count = 0;
                    // copy coding values for the population from the measure
                    if ((_g = measurePopulation.code) === null || _g === void 0 ? void 0 : _g.coding) {
                        const popCoding = (_h = measurePopulation.code) === null || _h === void 0 ? void 0 : _h.coding[0];
                        pop.code = {
                            coding: [
                                {
                                    system: popCoding.system,
                                    code: popCoding.code,
                                    display: popCoding.display
                                }
                            ]
                        };
                    }
                    (_j = group.population) === null || _j === void 0 ? void 0 : _j.push(pop);
                }
            });
            // if the measure definition has stratification, add group.stratifier
            if (measureGroup.stratifier) {
                group.stratifier = [];
                measureGroup.stratifier.forEach(s => {
                    var _a;
                    const reportStratifier = {};
                    if (s.code) {
                        reportStratifier.code = [s.code];
                    }
                    if (s.id) {
                        reportStratifier.id = s.id;
                    }
                    const strat = {};
                    // use existing populations, but reduce count as appropriate
                    // Deep copy population with matching attributes but different interface
                    strat.population = (JSON.parse(JSON.stringify(group.population)));
                    reportStratifier.stratum = [strat];
                    (_a = group.stratifier) === null || _a === void 0 ? void 0 : _a.push(reportStratifier);
                });
            }
            (_b = this.report.group) === null || _b === void 0 ? void 0 : _b.push(group);
        });
    }
    addAllResults(results) {
        results.forEach(result => {
            this.addPatientResults(result);
        });
    }
    addPatientResults(result) {
        // if this is a individual measure report and we have already received a patient we should throw
        // an error
        if (this.isIndividual && this.patientCount > 0) {
            throw new CustomErrors_1.UnsupportedProperty('The MeasureReport being built is an individual report and patient results have already been added');
        }
        if (!result.detailedResults) {
            throw new Error('ExecutionResults are missing detailedResults.');
        }
        // if we are creating an individual report create the patient reference.
        if (this.isIndividual) {
            const patId = `Patient/${result.patientId}`;
            const subjectReference = {
                reference: patId
            };
            this.report.subject = subjectReference;
        }
        result.detailedResults.forEach((groupResults, i) => {
            var _a, _b, _c, _d, _e, _f;
            if (this.isIndividual) {
                // add narrative for relevant clauses
                if ((0, DetailedResultsHelpers_1.isDetailedResult)(groupResults) && this.report.text && groupResults.html) {
                    this.report.text.div += groupResults.html;
                }
            }
            // find corresponding group in report
            // default to index if group is missing an ID
            let groupScoringCode = this.scoringCode;
            const group = ((_a = this.report.group) === null || _a === void 0 ? void 0 : _a.find(g => g.id == groupResults.groupId)) || ((_b = this.report.group) === null || _b === void 0 ? void 0 : _b[i]);
            if (!group) {
                throw new CustomErrors_1.UnexpectedProperty(`Group ${groupResults.groupId} not found in measure report`);
            }
            if (group.id) {
                const measureGroup = (_d = (_c = this.measure) === null || _c === void 0 ? void 0 : _c.group) === null || _d === void 0 ? void 0 : _d.find(g => g.id === group.id);
                if (measureGroup) {
                    groupScoringCode = this.getGroupScoringCode(measureGroup);
                }
            }
            // iterate over population results for episodes and increment the counters
            // TODO: handle EXM111 (doesn't identify itself as a episode of care measure). if it's an episode of care, you need to iterate over
            // stratifications : may need to clone results for one population group and adjust (in this case, just a straight clone)
            if (groupResults === null || groupResults === void 0 ? void 0 : groupResults.episodeResults) {
                groupResults.episodeResults.forEach(er => {
                    var _a, _b;
                    (_a = er.populationResults) === null || _a === void 0 ? void 0 : _a.forEach(pr => {
                        this.incrementPopulationInGroup(group, pr, groupScoringCode);
                    });
                    // TODO: update this when we support CV for summary/subject-list reports
                    // Consider moving measure score calculation out of this function
                    if (groupScoringCode === Enums_1.MeasureScoreType.CV) {
                        group.measureScore = this.calcMeasureScoreCV(this.measure, groupResults, group.id || '');
                        this.addScoreObservation(group.measureScore, this.measure, this.report);
                    }
                    // add to stratifier results if there are any
                    if (group.stratifier) {
                        (_b = er.stratifierResults) === null || _b === void 0 ? void 0 : _b.forEach(stratResults => {
                            var _a, _b, _c, _d;
                            // only add to results if this episode is in the strata
                            if (stratResults.result) {
                                // the strataCode has the potential to be a couple of things, either s.code[0].text (previous measures)
                                // or s.id (newer measures)
                                const strata = ((_a = group.stratifier) === null || _a === void 0 ? void 0 : _a.find(s => { var _a; return s.code && ((_a = s.code[0]) === null || _a === void 0 ? void 0 : _a.text) === stratResults.strataCode; })) ||
                                    ((_b = group.stratifier) === null || _b === void 0 ? void 0 : _b.find(s => s.id === stratResults.strataCode)); // strataCode may have an id value if code did not originally exist
                                const stratum = (_c = strata === null || strata === void 0 ? void 0 : strata.stratum) === null || _c === void 0 ? void 0 : _c[0];
                                if (stratum) {
                                    (_d = er.populationResults) === null || _d === void 0 ? void 0 : _d.forEach(pr => {
                                        this.incrementPopulationInStratum(stratum, pr, groupScoringCode);
                                    });
                                    // TODO: update this when we support CV for summary/subject-list reports
                                    if (groupScoringCode === Enums_1.MeasureScoreType.CV) {
                                        stratum.measureScore = this.calcMeasureScoreCV(this.measure, groupResults, group.id || '', stratResults.strataCode);
                                    }
                                }
                                else {
                                    throw new CustomErrors_1.UnexpectedProperty(`Stratum ${stratResults.strataCode} in group ${group.id} not found in measure reports`);
                                }
                            }
                        });
                    }
                });
            }
            else {
                (_e = groupResults.populationResults) === null || _e === void 0 ? void 0 : _e.forEach(pr => {
                    this.incrementPopulationInGroup(group, pr, groupScoringCode);
                });
                // TODO: update this when we support CV for summary/subject-list reports
                if (groupScoringCode === Enums_1.MeasureScoreType.CV) {
                    group.measureScore = this.calcMeasureScoreCV(this.measure, groupResults, group.id || '');
                    this.addScoreObservation(group.measureScore, this.measure, this.report);
                }
                // add to stratifier results if there are any
                if (group.stratifier) {
                    (_f = groupResults.stratifierResults) === null || _f === void 0 ? void 0 : _f.forEach(stratResults => {
                        var _a, _b, _c, _d;
                        // only add to results if this patient is in the strata
                        if (stratResults.appliesResult) {
                            // the strataCode has the potential to be a couple of things, either s.code[0].text (previous measures)
                            // or s.id (newer measures)
                            const strata = ((_a = group.stratifier) === null || _a === void 0 ? void 0 : _a.find(s => { var _a; return s.code && ((_a = s.code[0]) === null || _a === void 0 ? void 0 : _a.text) === stratResults.strataCode; })) ||
                                ((_b = group.stratifier) === null || _b === void 0 ? void 0 : _b.find(s => s.id === stratResults.strataCode));
                            const stratum = (_c = strata === null || strata === void 0 ? void 0 : strata.stratum) === null || _c === void 0 ? void 0 : _c[0];
                            if (stratum) {
                                (_d = groupResults.populationResults) === null || _d === void 0 ? void 0 : _d.forEach(pr => {
                                    this.incrementPopulationInStratum(stratum, pr, groupScoringCode);
                                });
                                // TODO: update this when we support CV for summary/subject-list reports
                                if (groupScoringCode === Enums_1.MeasureScoreType.CV) {
                                    stratum.measureScore = this.calcMeasureScoreCV(this.measure, groupResults, group.id || '', stratResults.strataCode);
                                }
                            }
                            else {
                                throw new CustomErrors_1.UnexpectedProperty(`Stratum ${stratResults.strataCode} in group ${group.id} not found in measure reports`);
                            }
                        }
                    });
                }
            }
        });
        if (this.calculateSDEs) {
            this.addSDE(result);
        }
        if (this.calculateRAVs) {
            this.addRAV(result);
        }
        this.addEvaluatedResources(result);
        this.patientCount++;
    }
    incrementPopulationInStratum(stratum, pr, groupScoringCode) {
        var _a, _b, _c;
        if (groupScoringCode === Enums_1.MeasureScoreType.RATIO) {
            if (pr.criteriaExpression === 'Numerator Observations') {
                // find numerator group population and add observation value to it
                const numerPop = (_a = stratum.population) === null || _a === void 0 ? void 0 : _a.find(pop => { var _a; return ((_a = pop.code) === null || _a === void 0 ? void 0 : _a.coding) && pop.code.coding[0].code === 'numerator'; });
                if (numerPop && pr.observations) {
                    // use aggregate method to get final value from observations array
                    if (!numerPop.count)
                        numerPop.count = 0;
                    numerPop.count += this.aggregate(this.numeratorAggregateMethod, pr.observations);
                }
            }
            else if (pr.criteriaExpression === 'Denominator Observations') {
                // find denominator group population and add observation value to it
                const denomPop = (_b = stratum.population) === null || _b === void 0 ? void 0 : _b.find(pop => { var _a; return ((_a = pop.code) === null || _a === void 0 ? void 0 : _a.coding) && pop.code.coding[0].code === 'denominator'; });
                if (denomPop && pr.observations) {
                    // use aggregate method to get final value from observations array
                    if (!denomPop.count)
                        denomPop.count = 0;
                    denomPop.count += this.aggregate(this.numeratorAggregateMethod, pr.observations);
                }
            }
        }
        else {
            const pop = (_c = stratum.population) === null || _c === void 0 ? void 0 : _c.find(pop => { var _a; return ((_a = pop.code) === null || _a === void 0 ? void 0 : _a.coding) && pop.code.coding[0].code === pr.populationType; });
            if (pop) {
                // add to pop count creating it if not already created.
                if (!pop.count)
                    pop.count = 0;
                pop.count += pr.result ? 1 : 0;
            }
        }
    }
    incrementPopulationInGroup(group, pr, groupScoringCode) {
        var _a, _b, _c;
        if (groupScoringCode === Enums_1.MeasureScoreType.RATIO && pr.populationType !== 'initial-population') {
            if (pr.criteriaExpression === 'Numerator Observations') {
                // find numerator group population and add observation value to it
                const numerPop = (_a = group.population) === null || _a === void 0 ? void 0 : _a.find(pop => { var _a; return ((_a = pop.code) === null || _a === void 0 ? void 0 : _a.coding) && pop.code.coding[0].code === 'numerator'; });
                if (numerPop && pr.observations) {
                    // use aggregate method to get final value from observations array
                    if (!numerPop.count)
                        numerPop.count = 0;
                    numerPop.count += this.aggregate(this.numeratorAggregateMethod, pr.observations);
                }
            }
            else if (pr.criteriaExpression === 'Denominator Observations') {
                // find denominator group population and add observation value to it
                const denomPop = (_b = group.population) === null || _b === void 0 ? void 0 : _b.find(pop => { var _a; return ((_a = pop.code) === null || _a === void 0 ? void 0 : _a.coding) && pop.code.coding[0].code === 'denominator'; });
                if (denomPop && pr.observations) {
                    // use aggregate method to get final value from observations array
                    if (!denomPop.count)
                        denomPop.count = 0;
                    denomPop.count += this.aggregate(this.numeratorAggregateMethod, pr.observations);
                }
            }
        }
        else {
            const pop = (_c = group.population) === null || _c === void 0 ? void 0 : _c.find(pop => { var _a; return ((_a = pop.code) === null || _a === void 0 ? void 0 : _a.coding) && pop.code.coding[0].code === pr.populationType; });
            if (pop) {
                // add to pop count creating it if not already created.
                if (!pop.count)
                    pop.count = 0;
                pop.count += pr.result ? 1 : 0;
            }
            else {
                throw new CustomErrors_1.UnexpectedProperty(`Population ${pr.populationType} in group ${group.id} not found in measure report.`);
            }
        }
    }
    addSDE(result) {
        var _a;
        (_a = result.supplementalData) === null || _a === void 0 ? void 0 : _a.forEach(sd => {
            this.addSingleSDEResult(sd);
        });
    }
    addRAV(result) {
        var _a;
        (_a = result.riskAdjustment) === null || _a === void 0 ? void 0 : _a.forEach(ra => {
            this.addSingleSDEResult(ra);
        });
    }
    // 	Note that supplemental data are reported as observations for each patient and included in the evaluatedResources bundle. See the MeasureReport resource or the Quality Reporting topic for more information.
    addSingleSDEResult(sd) {
        var _a, _b, _c, _d, _e;
        if (sd && sd.rawResult) {
            const observation = {};
            observation.resourceType = 'Observation';
            observation.code = { text: sd.name };
            observation.id = (0, uuid_1.v4)();
            observation.status = 'final';
            observation.extension = [
                {
                    url: 'http://hl7.org/fhir/StructureDefinition/cqf-measureInfo',
                    extension: [
                        {
                            url: 'measure',
                            valueCanonical: this.measure.url
                        },
                        {
                            url: 'populationId',
                            valueString: sd.name
                        }
                    ]
                }
            ];
            // add coding to valueCodeableConcept
            if ('forEach' in sd.rawResult) {
                observation.valueCodeableConcept = { coding: [] };
                (_a = sd.rawResult) === null || _a === void 0 ? void 0 : _a.forEach((rr) => {
                    var _a, _b, _c, _d, _e, _f, _g;
                    if (((_a = rr.code) === null || _a === void 0 ? void 0 : _a.value) && ((_b = rr.system) === null || _b === void 0 ? void 0 : _b.value) && ((_c = rr.display) === null || _c === void 0 ? void 0 : _c.value)) {
                        // create supplemental data elements
                        (_e = (_d = observation.valueCodeableConcept) === null || _d === void 0 ? void 0 : _d.coding) === null || _e === void 0 ? void 0 : _e.push({
                            system: rr.system.value,
                            code: rr.code.value,
                            display: rr.display.value
                        });
                    }
                    else if (rr.isCode) {
                        // if a CQL system code is returned
                        (_g = (_f = observation.valueCodeableConcept) === null || _f === void 0 ? void 0 : _f.coding) === null || _g === void 0 ? void 0 : _g.push({
                            system: rr.system,
                            code: rr.code,
                            display: rr.display
                        });
                    }
                });
            }
            else if (sd.rawResult.isCode) {
                observation.valueCodeableConcept = { coding: [] };
                (_c = (_b = observation.valueCodeableConcept) === null || _b === void 0 ? void 0 : _b.coding) === null || _c === void 0 ? void 0 : _c.push({
                    system: sd.rawResult.system,
                    code: sd.rawResult.code,
                    display: sd.rawResult.display
                });
            }
            // add as evaluated resource reference
            (_d = this.report.contained) === null || _d === void 0 ? void 0 : _d.push(observation);
            (_e = this.report.evaluatedResource) === null || _e === void 0 ? void 0 : _e.push({
                reference: `#${observation.id}`
            });
        }
    }
    populationTotal(population, type) {
        var _a;
        return (((_a = population === null || population === void 0 ? void 0 : population.find(pop => {
            var _a, _b;
            return ((_a = pop.code) === null || _a === void 0 ? void 0 : _a.coding) && pop.code.coding.length > 0 && ((_b = pop.code) === null || _b === void 0 ? void 0 : _b.coding[0].code) === type;
        })) === null || _a === void 0 ? void 0 : _a.count) || 0.0);
    }
    median(observations) {
        const sorted = observations.sort((a, b) => {
            return a - b;
        });
        const centerIndex = Math.floor(sorted.length / 2);
        if (sorted.length % 2 === 0) {
            return 0.5 * (sorted[centerIndex - 1] + sorted[centerIndex]);
        }
        else {
            return sorted[centerIndex];
        }
    }
    // http://build.fhir.org/ig/HL7/cqf-measures/ValueSet-aggregate-method.html
    aggregate(aggregation, observations) {
        // Note: add break for any non-returning case
        switch (aggregation) {
            case Enums_1.AggregationType.SUM:
                // sum	Sum	The measure score is determined by adding together the observations derived from the measure population.
                return observations.reduce((accumulator, currentValue) => accumulator + currentValue);
            case Enums_1.AggregationType.AVERAGE:
                // average	Average	The measure score is determined by taking the average of the observations derived from the measure population.
                return observations.reduce((accumulator, currentValue) => accumulator + currentValue) / observations.length;
            case Enums_1.AggregationType.MEDIAN:
                // median	Median	The measure score is determined by taking the median of the observations derived from the measure population.
                return this.median(observations);
            case Enums_1.AggregationType.MIN:
                // minimum	Minimum	The measure score is determined by taking the minimum of the observations derived from the measure population.
                return Math.min(...observations);
            case Enums_1.AggregationType.MAX:
                // maximum	Maximum	The measure score is determined by taking the maximum of the observations derived from the measure population.
                return Math.max(...observations);
            case Enums_1.AggregationType.COUNT:
                // count	Count	The measure score is determined as the number of observations derived from the measure population.
                return observations.length;
            default:
                throw new CustomErrors_1.UnsupportedProperty(`Measure score aggregation type \"${aggregation}\" not supported`);
        }
    }
    // CV requires different input types than other scores
    calcMeasureScoreCV(measure, detail, groupID, strataCode) {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        let observations = [];
        if (detail.episodeResults) {
            // find all episode results with a true measure observation
            const relevantEpisodes = ((_a = detail.episodeResults) === null || _a === void 0 ? void 0 : _a.filter(er => {
                var _a, _b, _c;
                // ignore stratification (by setting as true) if stratifier not passed in
                const inStrat = strataCode ? (_b = (_a = er.stratifierResults) === null || _a === void 0 ? void 0 : _a.find(sr => sr.strataCode == strataCode)) === null || _b === void 0 ? void 0 : _b.result : true;
                return ((_c = er.populationResults.find(pr => pr.populationType === Enums_1.PopulationType.OBSERV)) === null || _c === void 0 ? void 0 : _c.result) && inStrat;
            })) || [];
            // Note: only uses first of potentially multiple observations in each episode (since we can't tell which observation is which)
            // if there are multiple oservations, then this may cause inconsistent behavior
            observations = relevantEpisodes.map(er => { var _a; return (_a = er.populationResults.find(pr => pr.populationType === Enums_1.PopulationType.OBSERV)) === null || _a === void 0 ? void 0 : _a.observations[0]; });
        }
        else {
            // ignore stratification (by setting as true) if stratifier not passed in
            const inStrat = strataCode ? (_c = (_b = detail.stratifierResults) === null || _b === void 0 ? void 0 : _b.find(sr => sr.strataCode === strataCode)) === null || _c === void 0 ? void 0 : _c.result : true;
            // CV for patient-based results is untested
            const obsResultPop = (_d = detail.populationResults) === null || _d === void 0 ? void 0 : _d.find(pr => pr.populationType === Enums_1.PopulationType.OBSERV);
            observations = (obsResultPop === null || obsResultPop === void 0 ? void 0 : obsResultPop.result) && inStrat ? [obsResultPop.observations[0]] : [];
        }
        // find aggregation type
        const measureGroup = (_e = measure.group) === null || _e === void 0 ? void 0 : _e.find(g => g.id === groupID);
        const observPop = (_f = measureGroup === null || measureGroup === void 0 ? void 0 : measureGroup.population) === null || _f === void 0 ? void 0 : _f.find(p => { var _a, _b; return (_b = (_a = p.code) === null || _a === void 0 ? void 0 : _a.coding) === null || _b === void 0 ? void 0 : _b.find(c => c.code === 'measure-observation'); });
        const aggregation = ((_h = (_g = observPop === null || observPop === void 0 ? void 0 : observPop.extension) === null || _g === void 0 ? void 0 : _g.find(e => e.url === 'http://hl7.org/fhir/us/cqfmeasures/StructureDefinition/cqfm-aggregateMethod')) === null || _h === void 0 ? void 0 : _h.valueCode) || '';
        // Note: unit not currently available in data, so not included in this quantity (should be inferable from measure to whoever's using the score)
        // Score also captured in evaluated resource observation
        return {
            value: this.aggregate(aggregation, observations) //|| 0 TODO: set to alternative value if null (no relevant episodes)?
        };
    }
    // TODO: shouldn't this have a group reference or something since the score is specific to a group? (how would this score be re-associated with the right group?)
    addScoreObservation(score, measure, report) {
        var _a, _b;
        // create observation resource to reflect the score value and add as reference in evaluated resources
        const observationResource = {
            resourceType: 'Observation',
            code: {
                text: 'MeasureObservation'
            },
            id: (0, uuid_1.v4)(),
            status: 'final',
            valueQuantity: score
        };
        // is this extension necessary?
        observationResource.extension = [
            {
                url: 'http://hl7.org/fhir/StructureDefinition/cqf-measureInfo',
                extension: [
                    {
                        url: 'measure',
                        valueCanonical: measure.url
                    },
                    {
                        url: 'populationId',
                        valueString: 'MeasureObservation'
                    }
                ]
            }
        ];
        (_a = report.contained) === null || _a === void 0 ? void 0 : _a.push(observationResource);
        (_b = report.evaluatedResource) === null || _b === void 0 ? void 0 : _b.push({
            reference: `#${observationResource.id}`
        });
    }
    calcMeasureScore(scoringCode, population) {
        switch (scoringCode) {
            case Enums_1.MeasureScoreType.PROP:
                // (Numerator - Numerator Exclusions) / (Denominator - D Exclusions - D Exceptions).
                const numeratorCount = this.populationTotal(population, Enums_1.PopulationType.NUMER) -
                    this.populationTotal(population, Enums_1.PopulationType.NUMEX);
                const denominatorCount = this.populationTotal(population, Enums_1.PopulationType.DENOM) -
                    this.populationTotal(population, Enums_1.PopulationType.DENEX) -
                    this.populationTotal(population, Enums_1.PopulationType.DENEXCEP);
                return {
                    // TODO: what if value for denominator 0? ... do we need to subtract denex, dexecep... probably, as https://ecqi.healthit.gov/system/files/eCQM-Logic-and-Guidance-2018-0504.pdf
                    value: denominatorCount === 0 ? 0 : (numeratorCount / denominatorCount) * 1.0
                };
            case Enums_1.MeasureScoreType.COHORT:
                // Note: Untested measure score type
                return {
                    value: this.populationTotal(population, Enums_1.PopulationType.IPP) * 1.0
                };
            case Enums_1.MeasureScoreType.RATIO:
                // numerator count represents aggregation of all numerator observations
                const numeratorCount2 = this.populationTotal(population, Enums_1.PopulationType.NUMER);
                // denominator count represents aggregation of all denominator observations
                const denominatorCount2 = this.populationTotal(population, Enums_1.PopulationType.DENOM);
                return {
                    value: denominatorCount2 === 0 ? 0 : (numeratorCount2 / denominatorCount2) * 1.0
                };
            default:
                throw new CustomErrors_1.UnsupportedProperty(`Measure score type \"${scoringCode}\" not supported`);
        }
    }
    calculateGroupScores() {
        var _a;
        if (!this.isIndividual && this.scoringCode === Enums_1.MeasureScoreType.CV) {
            throw new CustomErrors_1.UnsupportedProperty('Aggregate measure reports for continuous variable measures not supported');
        }
        (_a = this.report.group) === null || _a === void 0 ? void 0 : _a.forEach(group => {
            var _a, _b;
            let groupScoringCode = this.scoringCode;
            if (group.id) {
                const measureGroup = (_a = this.measure.group) === null || _a === void 0 ? void 0 : _a.find(g => g.id === group.id);
                if (measureGroup) {
                    groupScoringCode = this.getGroupScoringCode(measureGroup);
                }
            }
            if (group.population) {
                if (groupScoringCode === Enums_1.MeasureScoreType.CV) {
                    //this...
                }
                else {
                    group.measureScore = this.calcMeasureScore(groupScoringCode, group.population);
                }
            }
            else {
                throw new CustomErrors_1.UnexpectedProperty(`Group ${group.id} is missing population results.`);
            }
            // calculate all stratifiers in group
            (_b = group.stratifier) === null || _b === void 0 ? void 0 : _b.forEach(strat => {
                var _a;
                (_a = strat.stratum) === null || _a === void 0 ? void 0 : _a.forEach(stratum => {
                    if (stratum.population) {
                        if (groupScoringCode === Enums_1.MeasureScoreType.CV) {
                            //
                        }
                        else {
                            stratum.measureScore = this.calcMeasureScore(groupScoringCode, stratum.population);
                        }
                    }
                    else {
                        throw new CustomErrors_1.UnexpectedProperty(`Group ${group.id} Stratifier ${strat.id} is missing population results.`);
                    }
                });
            });
        });
    }
    getReport() {
        this.calculateGroupScores();
        return this.report;
    }
    static buildMeasureReports(measureBundle, executionResults, options) {
        const reports = [];
        const measure = (0, MeasureBundleHelpers_1.extractMeasureFromBundle)(measureBundle);
        executionResults.forEach(result => {
            const builder = new MeasureReportBuilder(measure, options);
            builder.addPatientResults(result);
            reports.push(builder.getReport());
        });
        return reports;
    }
}
exports.default = MeasureReportBuilder;
//# sourceMappingURL=MeasureReportBuilder.js.map